import { inject, Injectable } from "@angular/core";
import { ApiService } from "./base-api/api.service";
import { map, Observable } from "rxjs";
import { ScholarshipApplyResponse } from "../interfaces/scholarship-apply-response";
import { ApplicationSubmitRequest, DocumentUploadResponse } from "../interfaces/application-submit-request";
import { AdminApplication } from '../models/admin-application';
import { ApplicationDecision } from '../models/application-decision';
import { ApplicationReview } from '../models/application-review';
import { StudentApplication } from '../models/student-application';
import { StudentDocument } from '../models/student-document';
import { VerificationDocument } from '../models/verification-document';

@Injectable({
    providedIn: 'root',
})
export class ScholarshipApplicationApiService {
    private readonly api = inject(ApiService);

    checkApplyEligibility(scholarshipId: number): Observable<ScholarshipApplyResponse> {
        return this.api.get<{ data: ScholarshipApplyResponse }>(`scholarships/${scholarshipId}/apply`).pipe(
            map(response => response.data)
        );
    }

    getDraft(scholarshipId: number): Observable<{ payloadJson: string; currentStep: number } | null> {
        // No saved draft is reported as an empty result rather than a 404, so
        // unwrap the envelope and normalise the absence to null.
        return this.api
            .get<{ data: { payloadJson: string; currentStep: number } | null }>(`scholarships/${scholarshipId}/application-draft`)
            .pipe(map(response => response?.data ?? null));
    }

    saveDraft(scholarshipId: number, payloadJson: string, currentStep: number): Observable<{ saved: boolean }> {
        return this.api.put(`scholarships/${scholarshipId}/application-draft`, { payloadJson, currentStep });
    }

    submitApplication(scholarshipId: number, request: ApplicationSubmitRequest): Observable<{ id: number }> {
        // The builder/UI works with `label`/`value`, but the backend's
        // ApplicationFieldDto — and the eligibility evaluator, which reads
        // `FieldValue` off the stored payload — expect the fully-qualified
        // field names, so translate before sending.
        const payload = {
            ...request,
            applicationDetails: request.applicationDetails.map(detail => ({
                categoryName: detail.categoryName,
                fieldResponses: detail.fieldResponses.map(({ fieldId, fieldKey, label, value }) => ({
                    fieldId,
                    fieldKey,
                    fieldLabel: label,
                    fieldValue: value,
                })),
            })),
        };
        return this.api.post(`scholarships/${scholarshipId}/applications`, payload);
    }

    uploadDocument(scholarshipId: number, file: File): Observable<DocumentUploadResponse> {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return this.api.post(`scholarships/${scholarshipId}/documents`, formData);
    }

    getMyApplications(): Observable<StudentApplication[]> {
        return this.api.get<StudentApplication[]>('applications/my-applications');
    }

    getMyDocuments(): Observable<StudentDocument[]> {
        return this.api.get<StudentDocument[]>('documents/my-documents');
    }

    downloadDocument(objectKey: string): Observable<Blob> {
        return this.api.get<Blob>(`documents/download?objectKey=${encodeURIComponent(objectKey)}`, {
            responseType: 'blob',
        });
    }

    getApplicationsForReview(): Observable<AdminApplication[]> {
        return this.api.get<{ data: AdminApplication[] }>('applications').pipe(
            map(response => response.data)
        );
    }

    /**
     * Every application for one scholarship, whatever its status. Used by the
     * per-scholarship administration page, which shows the whole cycle rather
     * than only the applications still awaiting a decision.
     */
    getApplicationsForScholarship(scholarshipId: number): Observable<AdminApplication[]> {
        return this.api.get<{ data: AdminApplication[] }>(`scholarships/${scholarshipId}/applications`).pipe(
            map(response => response.data)
        );
    }

    getApplicationReview(id: number): Observable<ApplicationReview> {
        return this.api.get<{ data: ApplicationReview }>(`applications/${id}`).pipe(map(response => response.data));
    }

    reviewApplication(id: number, decision: ApplicationDecision): Observable<ApplicationReview> {
        return this.api.put<{ data: ApplicationReview }>(`applications/${id}/review`, { decision }).pipe(map(response => response.data));
    }

    reviewDocument(id: number, documentId: number, decision: 'Approved' | 'Rejected'): Observable<ApplicationReview> {
        return this.api.put<{ data: ApplicationReview }>(`applications/${id}/documents/${documentId}/review`, { decision }).pipe(map(response => response.data));
    }

    downloadApplicationDocument(id: number, documentId: number): Observable<Blob> {
        return this.api.get<Blob>(`applications/${id}/documents/${documentId}/download`, { responseType: 'blob' });
    }

    getDocumentsForVerification(): Observable<VerificationDocument[]> {
        return this.api.get<{ data: VerificationDocument[] }>('documents/verification').pipe(map(response => response.data));
    }
}
